import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Loader2, Mail, Paperclip, X, Eye, Send, Users, UserCheck, LifeBuoy,
  AlertCircle, Bold, Italic, List, RefreshCcw, Save, History,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const KINDS = [
  { value: 'announcement', label: 'Company Announcement' },
  { value: 'memo', label: 'Memorandum' },
  { value: 'schedule_update', label: 'Schedule / Location Update' },
  { value: 'payment', label: 'Payment / Operational Notice' },
  { value: 'cancellation', label: 'Event Cancellation' },
];
const MAX_ATTACH_BYTES = 8 * 1024 * 1024; // 8MB total

const SendEventEmailModal = ({ open, onClose, shift }) => {
  const shiftId = shift?.id;
  const [loading, setLoading] = useState(false);
  const [roster, setRoster] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({}); // talent_id -> true
  const [subject, setSubject] = useState('');
  const [kind, setKind] = useState('announcement');
  const [cancellationReason, setCancellationReason] = useState('');
  const [attachments, setAttachments] = useState([]); // {filename, content_b64, content_type, size_bytes}
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const editorRef = useRef(null);
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const [r, t, h] = await Promise.all([
        api.get(`/admin/shifts/${shiftId}/email-recipients`),
        api.get('/admin/event-email-templates'),
        api.get(`/admin/shifts/${shiftId}/email-history`),
      ]);
      setRoster(r.data);
      setTemplates(t.data.templates || []);
      setHistory(h.data.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    if (open) {
      setSelected({}); setSubject(''); setKind('announcement'); setCancellationReason('');
      setAttachments([]); setPreview(false); setResult(null); setShowHistory(false);
      if (editorRef.current) editorRef.current.innerHTML = '';
      loadAll();
    }
  }, [open, loadAll]);

  const confirmed = roster?.confirmed || [];
  const backups = roster?.backups || [];
  const clientPresent = roster?.client?.present;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectGroup = (list, on) => setSelected((s) => {
    const next = { ...s };
    list.forEach((t) => { next[t.talent_id] = on; });
    return next;
  });
  const allStaff = [...confirmed, ...backups];
  const selectedCount = allStaff.filter((t) => selected[t.talent_id]).length;

  const handleFiles = async (files) => {
    const current = attachments.reduce((a, f) => a + (f.size_bytes || 0), 0);
    let total = current;
    const added = [];
    for (const file of Array.from(files)) {
      total += file.size;
      if (total > MAX_ATTACH_BYTES) {
        toast.error('Attachments exceed 8MB total'); break;
      }
      const b64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(String(reader.result).split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      added.push({ filename: file.name, content_b64: b64, content_type: file.type || 'application/octet-stream', size_bytes: file.size });
    }
    setAttachments((a) => [...a, ...added]);
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => String(x.id) === String(tid));
    if (!t) return;
    setSubject(t.subject || '');
    if (editorRef.current) editorRef.current.innerHTML = t.body_html || '';
  };

  const exec = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); };

  const bodyHtml = () => (editorRef.current?.innerHTML || '').trim();

  const doSend = async () => {
    const ids = allStaff.filter((t) => selected[t.talent_id]).map((t) => t.talent_id);
    if (!ids.length) { toast.error('Select at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Enter a subject'); return; }
    if (!bodyHtml()) { toast.error('Enter a message'); return; }
    if (!clientPresent) {
      toast.error('Client has no email on file — add it before sending (required BCC).');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/shifts/${shiftId}/send-email`, {
        subject: subject.trim(),
        body_html: bodyHtml(),
        kind,
        cancellation_reason: kind === 'cancellation' ? cancellationReason : null,
        talent_ids: ids,
        attachments,
        also_in_app: true,
      });
      setResult(data);
      toast.success(`Sent to ${data.sent} recipient(s)${data.failed ? `, ${data.failed} failed` : ''} · client BCC'd`);
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'client_email_missing') {
        toast.error(detail.message, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Send failed');
      }
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async () => {
    if (!subject.trim() || !bodyHtml()) { toast.error('Subject and message required to save a template'); return; }
    const name = window.prompt('Template name:');
    if (!name) return;
    try {
      await api.post('/admin/event-email-templates', {
        name, subject: subject.trim(), body_html: bodyHtml(),
        category: kind === 'cancellation' ? 'event_cancellation' : 'event_communication',
      });
      toast.success('Template saved');
      const t = await api.get('/admin/event-email-templates');
      setTemplates(t.data.templates || []);
    } catch (e) { toast.error('Failed to save template'); }
  };

  const resendFailed = async (commId) => {
    try {
      const { data } = await api.post(`/admin/event-communications/${commId}/resend-failed`);
      toast.success(`Resent ${data.resent} failed message(s)`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resend failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="event-email-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal-600" /> Send Email — {shift?.venue_name || 'Event'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
        ) : (
          <div className="space-y-4">
            {/* Client BCC banner */}
            {clientPresent ? (
              <div className="flex items-center gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-sm text-teal-800" data-testid="client-bcc-banner">
                <UserCheck className="w-4 h-4" />
                Client <strong>{roster?.client?.name || roster?.client?.email}</strong> will be automatically BCC'd (hidden from talent).
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" data-testid="client-missing-banner">
                <AlertCircle className="w-4 h-4" />
                This event's client has no email on file. Add one before sending — the client copy is required.
              </div>
            )}

            {/* Recipients */}
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Recipients ({selectedCount} selected)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => selectGroup(allStaff, true)} data-testid="select-all-staff-btn">
                  <Users className="w-3.5 h-3.5 mr-1" /> All staff ({allStaff.length})
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => selectGroup(confirmed, true)} data-testid="select-confirmed-btn">
                  <UserCheck className="w-3.5 h-3.5 mr-1" /> Confirmed ({confirmed.length})
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => selectGroup(backups, true)} data-testid="select-backups-btn">
                  <LifeBuoy className="w-3.5 h-3.5 mr-1" /> Backups ({backups.length})
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSelected({})} data-testid="clear-recipients-btn">Clear</Button>
              </div>
              <div className="mt-2 max-h-36 overflow-y-auto border rounded-lg divide-y">
                {allStaff.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No booked talent on this shift yet.</div>}
                {allStaff.map((t) => (
                  <label key={t.talent_id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`recipient-row-${t.talent_id}`}>
                    <Checkbox checked={!!selected[t.talent_id]} onCheckedChange={() => toggle(t.talent_id)} data-testid={`recipient-check-${t.talent_id}`} />
                    <span className="flex-1">{t.name}</span>
                    {backups.includes(t) && <span className="text-[10px] uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">Backup</span>}
                  </label>
                ))}
              </div>
            </div>

            {/* Kind + template */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-500">Type</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="mt-1" data-testid="email-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-500">Template</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="mt-1" data-testid="email-template-select"><SelectValue placeholder="Insert template…" /></SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No saved templates</div>}
                    {templates.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {kind === 'cancellation' && (
              <Input placeholder="Cancellation reason (e.g. Client cancelled, weather)" value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)} data-testid="cancellation-reason-input" />
            )}

            {/* Subject */}
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line"
                className="mt-1" data-testid="email-subject-input" />
            </div>

            {/* Rich text editor */}
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Message</Label>
              <div className="mt-1 border rounded-lg overflow-hidden">
                <div className="flex gap-1 border-b bg-slate-50 px-2 py-1">
                  <button type="button" onClick={() => exec('bold')} className="p-1 hover:bg-slate-200 rounded" data-testid="editor-bold"><Bold className="w-4 h-4" /></button>
                  <button type="button" onClick={() => exec('italic')} className="p-1 hover:bg-slate-200 rounded" data-testid="editor-italic"><Italic className="w-4 h-4" /></button>
                  <button type="button" onClick={() => exec('insertUnorderedList')} className="p-1 hover:bg-slate-200 rounded" data-testid="editor-list"><List className="w-4 h-4" /></button>
                </div>
                <div ref={editorRef} contentEditable suppressContentEditableWarning
                  className="min-h-[140px] p-3 text-sm focus:outline-none prose prose-sm max-w-none"
                  data-testid="email-body-editor" />
              </div>
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase font-semibold text-slate-500">Attachments (PDF, images · max 8MB)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="attach-file-btn">
                  <Paperclip className="w-3.5 h-3.5 mr-1" /> Attach
                </Button>
                <input ref={fileRef} type="file" multiple accept=".pdf,image/*" className="hidden"
                  onChange={(e) => handleFiles(e.target.files)} data-testid="attach-file-input" />
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1" data-testid={`attachment-${i}`}>
                      <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                      <span className="flex-1 truncate">{a.filename}</span>
                      <span className="text-xs text-slate-400">{Math.round((a.size_bytes || 0) / 1024)}KB</span>
                      <button type="button" onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))} data-testid={`remove-attachment-${i}`}><X className="w-3.5 h-3.5 text-slate-500" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preview */}
            {preview && (
              <div className="rounded-lg border bg-white p-4" data-testid="email-preview">
                <div className="text-xs text-slate-400 mb-1">PREVIEW · {selectedCount} talent · Client BCC: {roster?.client?.email || '—'}</div>
                <div className="font-semibold text-slate-900 mb-2">{subject || '(no subject)'}</div>
                <div className="text-sm text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: bodyHtml() || '<em>(empty)</em>' }} />
              </div>
            )}

            {result && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="send-result">
                Sent {result.sent} · Failed {result.failed} · Client BCC'd ({result.client_bcc}). Status: {result.status}.
              </div>
            )}

            {/* History toggle */}
            <button type="button" onClick={() => setShowHistory((s) => !s)} className="flex items-center gap-1 text-sm text-teal-700" data-testid="toggle-history">
              <History className="w-4 h-4" /> Communication history ({history.length})
            </button>
            {showHistory && (
              <div className="space-y-1 border rounded-lg p-2 max-h-40 overflow-y-auto" data-testid="email-history-list">
                {history.length === 0 && <div className="text-xs text-slate-400 px-1">No emails sent for this event yet.</div>}
                {history.map((h) => (
                  <div key={h.id} className="flex items-center gap-2 text-xs px-1 py-1 border-b last:border-0" data-testid={`history-row-${h.id}`}>
                    <span className="flex-1 truncate">{h.subject}</span>
                    <span className="text-slate-400">{h.sent_count}✓ {h.failed_count > 0 ? `${h.failed_count}✗` : ''}</span>
                    <span className={`px-1.5 py-0.5 rounded ${h.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : h.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{h.status}</span>
                    {h.failed_count > 0 && (
                      <button type="button" onClick={() => resendFailed(h.id)} className="text-teal-700 flex items-center gap-0.5" data-testid={`resend-failed-${h.id}`}>
                        <RefreshCcw className="w-3 h-3" /> Resend
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button type="button" variant="ghost" onClick={saveTemplate} data-testid="save-template-btn"><Save className="w-4 h-4 mr-1" /> Save template</Button>
          <Button type="button" variant="outline" onClick={() => setPreview((p) => !p)} data-testid="preview-email-btn"><Eye className="w-4 h-4 mr-1" /> {preview ? 'Hide' : 'Preview'}</Button>
          <Button type="button" onClick={doSend} disabled={sending || !clientPresent} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="send-email-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendEventEmailModal;
